import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LogOut, ChevronLeft } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  Building2,
  Handshake,
  Calendar,
  MessageSquare,
  Wallet,
  TrendingUp,
  CircleDollarSign,
} from "lucide-react";

import ingroooveLogo from "../../assets/logos/ingroove-logo.png";
import omnyxLogoText from "../../assets/logos/omnyx-logo-text.png";

const sidebarConfig = {
  ingroove: {
    label: "Ingroove",
    logo: ingroooveLogo,
    color: "text-primary",
    items: [
      {
        name: "Overview",
        path: "/ingroove/overview",
        icon: <LayoutDashboard size={16} />,
      },
      { name: "Users", path: "/ingroove/users", icon: <Users size={16} /> },
      { name: "Hubs", path: "/ingroove/hubs", icon: <Building2 size={16} /> },
      {
        name: "Hub Partners",
        path: "/ingroove/hub-partners",
        icon: <Handshake size={16} />,
      },
      {
        name: "Events",
        path: "/ingroove/events",
        icon: <Calendar size={16} />,
      },
      {
        name: "Chat Rooms",
        path: "/ingroove/chat-rooms",
        icon: <MessageSquare size={16} />,
      },
      {
        name: "Payouts",
        path: "/ingroove/payouts",
        icon: <Wallet size={16} />,
      },
      {
        name: "Financials",
        path: "/ingroove/financials",
        icon: <TrendingUp size={16} />,
      },
      {
        name: "System Wallet",
        path: "/ingroove/system-wallet",
        icon: <CircleDollarSign size={16} />,
      },
    ],
  },
};

const ProductLayout = ({ product }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const config = sidebarConfig[product];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (u) =>
    `${u?.name?.[0] ?? ""}${u?.surname?.[0] ?? ""}`.toUpperCase();

  return (
    <div className="flex h-screen bg-bg-base overflow-hidden">
      {/* Sidebar */}
      <div className="w-56 bg-bg-topbar border-r border-border flex flex-col flex-shrink-0">
        {/* Top */}
        <div className="p-4 border-b border-border">
          {/* Omnyx wordmark */}
          <div className="flex items-center gap-2 mb-4">
            <img
              src={omnyxLogoText}
              alt="Omnyx"
              className="h-7 object-contain rounded-md"
            />
          </div>

          {/* Product pill */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 w-full text-left group"
          >
            <ChevronLeft
              size={13}
              className="text-text-hint group-hover:text-primary transition"
            />
            <span className="text-xs text-text-muted group-hover:text-primary transition">
              All apps
            </span>
          </button>

          <div className="flex items-center gap-2 mt-3 px-2 py-1.5 bg-bg-surface border border-border rounded-lg">
            <img
              src={config.logo}
              alt={product.name}
              className="w-7 h-7 object-contain"
            />
            <span className={`text-sm font-semibold ${config.color}`}>
              {config.label}
            </span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {config.items.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition group relative ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-text-muted hover:bg-bg-elevated hover:text-text-secondary"
                }`
              }
            >
              {item.icon}
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* User + logout */}
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg">
            <div className="w-7 h-7 rounded-full omnyx-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {getInitials(user)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-text-secondary truncate">
                {user?.name} {user?.surname}
              </p>
              <p className="text-xs text-text-hint truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs text-red-400 hover:bg-red-500/10 transition mt-1"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <div className="h-12 bg-bg-topbar border-b border-border flex items-center px-6 justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span
              className="hover:text-primary cursor-pointer transition"
              onClick={() => navigate("/")}
            >
              Omnyx
            </span>
            <span>/</span>
            <span className={`font-medium ${config.color}`}>
              {config.label}
            </span>
          </div>
          <div className="text-xs text-text-hint">
            {new Date().toLocaleDateString("en-ZA", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default ProductLayout;
